import * as React from 'react';

import { Dispatch } from 'redux';
import { connect, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';
import { CREATE_ONBOARDING } from '@webapp/store/onboarding/actions';
import {
  SearchExternalArtists,
  ValidateExternalArtistId,
} from '@webapp/store/artist/action-creators';
import { CreateOnboarding } from '@webapp/store/onboarding/action-creators';

import styled, { useTheme } from 'styled-components';

import { Option } from '@webapp/types/common/option';
import SimpleArtist from '@common/types/artist/simple-artist';
import { NewInstance } from '@common/types/data/new-instance';

import { validateArtist } from '@webapp/utils/validators/artist';

import DropdownCtrl, {
  GenericDropdownBuilder,
} from '@webapp/controllers/form/dropdown';
import ArtistEditorCtrl from '@webapp/controllers/artist/artist-editor/index';
import { ArtistSearchResult } from '@services/artist/types/artist-search-result';
import { ArtistSearchResponse } from '@services/artist/types/artist-search-response';
import { ArtistRelation } from '@common/types/artist/artist-relation';

import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import Typography from '@components/basics/typography';
import ButtonArrow from '@components/actions/arrow-button';
import DropdownItem from '@components/form/dropdown/dropdown-item';
import CircleRightArrowIcon from '@components/basics/icons/circle-right-arrow';
import Hint from '@components/basics/hint';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';

import { RoleOptions } from '@webapp/pages/artist-profile/artist-team/invite-team-member-list';
import Avatar from '@components/basics/avatar';

interface CreateArtistCtrlProps {
  className?: string;
  isLoading: boolean;
  hideButton?: boolean;
  showErrors?: boolean;
  role: ArtistRelation['role'];
  onClick?: () => void;
  onChange?: (artistData: NewInstance<SimpleArtist>) => void;
  onChangeRole?: (role: ArtistRelation['role']) => void;
  isManagedArtist?: boolean;
  results?: ArtistSearchResponse;
  createOnboarding: (
    artist: NewInstance<SimpleArtist>,
    role: ArtistRelation['role']
  ) => CreateOnboarding;
  searchExternalArtists: (value: string) => SearchExternalArtists;
  validateExternalArtistId: (externalId: string) => ValidateExternalArtistId;
}

const mapStateToProps = (state: AppState) => ({
  results: state.artist.externalIds,
  isLoading: !!state.ui.loading[CREATE_ONBOARDING],
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createOnboarding: (artistData: SimpleArtist, role: ArtistRelation['role']) =>
    dispatch(new CreateOnboarding(artistData, role)),
  searchExternalArtists: (value: string) =>
    dispatch(new SearchExternalArtists(value)),
  validateExternalArtistId: (externalId: string) =>
    dispatch(new ValidateExternalArtistId(externalId)),
});

const RoleDropdown = GenericDropdownBuilder<ArtistRelation['role']>();

export const CreateArtistCtrl: React.FC<CreateArtistCtrlProps> = ({
  hideButton = false,
  showErrors = false,
  role,
  onChange,
  onChangeRole,
  ...props
}) => {
  const theme = useTheme();

  const [artist, setArtist] = React.useState<Option>();
  const [artistData, setArtistData] =
    React.useState<NewInstance<SimpleArtist>>();
  const [isOpen, setOpen] = React.useState(false);
  const [isNewArtist, setNewArtist] = React.useState(false);
  const [errorHint, setErrorHint] = React.useState(false);

  const isValidExternalId = useSelector(
    (state: AppState) =>
      state.artist.externalIdValidation.externalId === artist?.value &&
      state.artist.externalIdValidation.validationResult
  );

  React.useEffect(() => {
    if (onChange && artistData && validateArtist(artistData)) {
      onChange(artistData);
    }
  }, [artistData, onChange]);

  const handleArtistData = (data: NewInstance<SimpleArtist>) => {
    setOpen(true);
    setArtistData(data);
  };

  const getList = (value: string) => {
    props.searchExternalArtists(value);
  };

  const toggleNew = () => {
    const toggled = !isNewArtist;

    setNewArtist(toggled);
    setErrorHint(false);

    if (toggled) {
      setArtistData({
        ...(artistData as SimpleArtist),
        platforms: {
          ...artistData?.platforms,
          spotify: 'NEW',
        },
      });
    }
  };

  const handleSelect = (item: ArtistSearchResult) => {
    if (item) {
      setArtist({ label: item.name, value: item.id });
      props.validateExternalArtistId(item.id);

      const data: NewInstance<SimpleArtist> = {
        ...artistData,
        type: 'solo',
        shortId: '',
        name: item.name,
        avatar: item.avatar || '',
        externalIds: {
          spotify: item,
        },
        platforms: {
          spotify: `https://open.spotify.com/artist/${item.id}`,
        },
      };

      setArtistData(data);
      setOpen(false);
      setErrorHint(true);
    }
  };

  return (
    <>
      <Flex className={props.className || ''}>
        <Flex
          className="inputs-container"
          justifyContent="space-between"
          alignItems="center"
          gap={10}
        >
          <Grid className="inputs-wrapper" gap={5}>
            {isNewArtist ? (
              <NewArtist onClick={toggleNew}>
                <Typography
                  weight="700"
                  color={theme.colors.differentBackground}
                >
                  {artistData?.name}
                </Typography>
                <Flex justifyContent="flex-end" alignItems="center" gap={10}>
                  <Typography
                    uppercase
                    weight="700"
                    color={theme.colors.differentBackground}
                  >
                    <Translate id="ONBOARDING.ARTIST.NEW.TITLE" />
                  </Typography>
                  <CompletedIcon />
                </Flex>
              </NewArtist>
            ) : (
              <DropdownCtrl
                customBorderRadius="4px 4px 0 0"
                forceOpen={isOpen}
                close={!isOpen}
                value={artist?.label}
                header={
                  <ArtistEditorCtrl
                    name={artist?.label}
                    onChange={(data: NewInstance<SimpleArtist>) => {
                      handleArtistData(data);
                      getList(artistData?.name || '');
                    }}
                    onIncompleteData={() => setArtistData(undefined)}
                    disableAvatar
                    disableStreaming
                    hideStreamingPlatforms
                    showErrors={showErrors}
                  />
                }
                innerComponent={
                  <NewArtistOption
                    justifyContent="space-between"
                    alignItems="center"
                    onClick={toggleNew}
                  >
                    <Typography weight="700" color={theme.colors.blueColor}>
                      <Translate
                        id="ONBOARDING.ARTIST.INPUT.NEW.TITLE"
                        data={{ name: artistData?.name || '' }}
                      />
                    </Typography>
                    <CircleRightArrowIcon />
                  </NewArtistOption>
                }
              >
                <div>
                  <Typography
                    type="small"
                    className="mb-20"
                    color={theme.colors.tertiary}
                  >
                    <Translate id="ONBOARDING.ARTIST.INPUT.LIST.TITLE" />
                  </Typography>
                  {props.results?.spotify.map((item: ArtistSearchResult) => (
                    <DropdownItem
                      key={item.id}
                      decoration={
                        <Avatar
                          size="24"
                          src={item.avatar}
                          alt={`${item.name} avatar`}
                        />
                      }
                      onClick={() => {
                        handleSelect(item);
                      }}
                    >
                      {item.name}
                    </DropdownItem>
                  ))}
                </div>
              </DropdownCtrl>
            )}
            <DropdownWrapper>
              <RoleDropdown
                title={
                  <Translate id="ARTIST_PROFILE.TEAM.INPUT.DROPDOWN.LABEL" />
                }
                value={role}
                disabled={props.isManagedArtist}
                isGrey
                customBorderRadius="0 0 4px 4px"
                parentRelative={true}
                options={RoleOptions}
                onChange={onChangeRole}
              />
            </DropdownWrapper>
          </Grid>
          <ButtonArrow
            id="onboarding"
            onClick={props.onClick}
            className="button-arrow"
            isLoading={props.isLoading}
            disabled={
              !artistData ||
              !artistData.name.trim().length ||
              (!isValidExternalId && !isNewArtist)
            }
          />
        </Flex>
      </Flex>
      {artistData && errorHint && !isValidExternalId && !isNewArtist && (
        <Hint
          noMargin
          customTitle={<Translate id="ONBOARDING.ARTIST.HINT.TITLE" />}
          lightRedBackground={true}
        >
          <Translate id="ONBOARDING.ARTIST.HINT.TEXT1" />
          <Link
            style={{ textDecoration: 'underline', textDecorationColor: 'blue' }}
            to={{ pathname: 'mailto:support@trac.co' }}
            target="_blank"
          >
            <Translate id="ONBOARDING.ARTIST.HINT.TEXT2" />
          </Link>
          <Translate id="ONBOARDING.ARTIST.HINT.TEXT3" />
        </Hint>
      )}
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(CreateArtistCtrl)`
  .inputs-container {
    margin-bottom: 20px;
  }

  .inputs-container,
  .inputs-wrapper {
    width: 100%;
  }

  .button-arrow {
    max-width: 78px;
  }
`);

const NewArtistOption = styled(Flex)`
  cursor: pointer;
  padding: 18px 20px;
  background-color: ${({ theme }) => theme.colors.inputBackgroundAlternate};
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.inputBackgroundAlternateDark};
  }
`;

const NewArtist = styled(Flex)`
  cursor: pointer;
  padding: 0 20px;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  gap: 10px;
  border-radius: 4px 4px 0 0;
  background-color: ${({ theme }) => theme.colors.primary};

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    height: 60px;
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
`;
